// Lib
import React, { useMemo, useEffect } from 'react';
import { Typography, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';

// Components
import ShippingCard from '../ShippingCard';
import EditRequestButton from '../../../Button/EditRequestButton';
import Select from '../../../Field/Select';
import CartCard from '../../ShippingDetails/CartCard';
import ShippingAddresses from './ShippingAddresses';
import IconButton from '../../../Button/IconButton/IconButton';
import Spinner from '../../../Spinner';
import Feather from '../../../Feather';

// hooks
import useUI from '../../../../hooks/useUI';
import useRequest from '../../../../pages/Cart/hooks/useRequest';
import useUser from '../../../../hooks/useUser';
import useShippingAddresses from '../../../../pages/Cart/hooks/useShippingAddresses';
import useAddToKits from '../../../../pages/Kits/hooks/useAddToKits';
import useOverLimitProduct from '../../../../pages/Cart/hooks/useOverLimitProduct';

// Utils
import { isObjEmpty } from '../../../../utils/object';
import { pickIf } from '../../../../utils/pickIf';

// Constants
import { ORDER_INSTRUCTIONS } from '../../../../constants/cart';

// Selectors
import { productsObjSelector } from '../../../../store/selectors';

// Styles
import { useStyles } from './OrderInfoStyles';

const OrderInfoPageHeader = ({
  isDesktopMode,
  placedOrder,
  isRecurringOrder,
  isDistroOrder,
  onAddToMyKits,
  classes,
}) => {
  if (!placedOrder.id) {
    if (!isDesktopMode) {
      return null;
    }
    return (
      <Typography variant="h2" data-testid="review-order-page-header">
        Order Info
      </Typography>
    );
  }

  return (
    <>
      <div
        className={classes.placedTitle}
        data-testid="placed-order-page-header">
        Placed Order Info
      </div>
      {!isRecurringOrder && (
        <>
          <div
            className={classes.orderId}
            data-testid="placed-order-id-label">{`Order ID: #${
            placedOrder.order_sn || placedOrder.id
          }`}</div>
          {!isDistroOrder && (
            <IconButton
              disabled={!placedOrder.can_be_added_to_kit}
              onClick={onAddToMyKits}
              startIcon={<AddIcon />}
              data-testid="add-to-kits-button">
              Add To My Kits
            </IconButton>
          )}
        </>
      )}
    </>
  );
};

const ShippingRequestEditButton = ({
  shippingDetails,
  placedOrder,
  isAvailableMethod,
  userWithoutAllLimits,
  isMobileMode,
  isDistroOrder,
  openShippingRequestCard,
  classes,
}) => {
  const canEditShippingRequest =
    shippingDetails.method?.escalation &&
    !placedOrder?.id &&
    isAvailableMethod &&
    !userWithoutAllLimits;

  if (!canEditShippingRequest) {
    return null;
  }

  return (
    <EditRequestButton
      className={classes.editRequestButton}
      onClick={openShippingRequestCard}
      title={pickIf(
        isMobileMode && !isDistroOrder,
        'Edit Request',
        'Edit Shipping Request',
      )}
    />
  );
};

const ShippingMethodUnavailableError = ({
  placedOrder,
  isAvailableMethod,
  classes,
}) => {
  if (placedOrder?.id || isAvailableMethod) {
    return null;
  }

  return (
    <div className={classes.errorContainer}>
      <div>
        <Feather
          className={classes.warningIcon}
          nativeColor
          type="error"></Feather>
      </div>
      <div className={classes.error}>
        This method is unavailable. Please change shipping method.
      </div>
    </div>
  );
};

const PendingApprovalLabel = ({ placedOrder, classes }) => {
  if (!placedOrder?.additional_info_shipment) {
    return null;
  }

  return (
    <div
      className={classes.approvalInfo}
      data-testid="order-request-status-label">
      Pending Approval
    </div>
  );
};

const PlacedOrderInstructions = ({ placedOrder, classes }) => {
  if (!placedOrder.id || !placedOrder.shipping_comments) {
    return null;
  }

  return (
    <div className={classes.instructionsPlacedOrder}>
      <Typography
        className={classes.instructionsTitle}
        variant="h3"
        data-testid="special-instructions-header">
        Special Instructions
      </Typography>
      <div
        className={classes.shippingComment}
        data-testid="special-instructions-text">
        {placedOrder.shipping_comments}
      </div>
    </div>
  );
};

const OrderInstructionsSelect = ({
  placedOrder,
  shippingDetails,
  onChangeInstructions,
  classes,
}) => {
  if (placedOrder.id) {
    return null;
  }

  return (
    <>
      <div
        className={classes.instructions}
        data-testid="special-instructions-header">
        Special Instructions
      </div>
      <Select
        initialValue={shippingDetails.comment || ORDER_INSTRUCTIONS[0].title}
        placeholder="Select"
        className={classes.select}
        items={ORDER_INSTRUCTIONS}
        onChange={onChangeInstructions}
        data-testid="special-instructions-dropdown"
      />
    </>
  );
};

const ProductsTitle = ({
  placedOrder,
  withOverLimitRequest,
  savedRequest,
  openOverLimitCard,
  classes,
}) => {
  const showEditQtyRequest =
    withOverLimitRequest && !placedOrder.id && savedRequest;

  return (
    <Box
      className={classes.titleContainer}
      display="flex"
      justifyContent="space-between">
      <Typography
        className={classes.productsTitle}
        variant={pickIf(placedOrder.id, 'h3', 'h2')}>
        Products
      </Typography>
      {showEditQtyRequest && (
        <EditRequestButton
          onClick={openOverLimitCard}
          title="Edit Qty Request"
        />
      )}
    </Box>
  );
};

const CartCardsList = ({
  placedOrder,
  cart,
  shippingAddresses,
  disabledCountBtn,
  onClearOverLimitInDB,
  onSaveOverLimitInDB,
  productsObj,
  classes,
}) => {
  if (placedOrder.id) {
    return (
      <div className={classes.cartCard}>
        {placedOrder.order_items?.map(({ product, from_shared_kit }) => (
          <CartCard
            addressesCount={shippingAddresses.length}
            reviewOrderPage
            placedOrder={placedOrder}
            key={product.product_id}
            product={productsObj[product?.product_id || product]}
            disabledCountBtn={disabledCountBtn}
            fromSharedKit={!!from_shared_kit}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={classes.cartCard}>
      {cart.forOrder?.map(({ product, fromSharedKit }) => (
        <CartCard
          addressesCount={shippingAddresses.length}
          reviewOrderPage
          placedOrder={placedOrder}
          key={product}
          product={cart.productsObj[product]}
          disabledCountBtn={disabledCountBtn}
          onClearOverLimitInDB={onClearOverLimitInDB}
          onSaveOverLimitInDB={onSaveOverLimitInDB}
          fromSharedKit={fromSharedKit}
        />
      ))}
    </div>
  );
};

const OrderInfo = ({
  shippingDetails,
  goToSippingDetails = () => {},
  cart,
  onChangeInstructions = () => {},
  placedOrder = {},
  withOverLimitRequest,
  disabledCountBtn,
  isAvailableMethod = true,
  moreThanInStock,
}) => {
  const { productsObj, isLoading } = useSelector((state) => ({
    productsObj: productsObjSelector(state),
    isLoading: state.isLoading,
  }));
  const { isDistroOrder, shippingAddresses, goToAddresses, onDeleteAddress } =
    useShippingAddresses();
  const { isKiteUser, userWithoutAllLimits } = useUser();
  const { classes } = useStyles({
    isDistroOrder,
    placedOrder: placedOrder.id,
    isKiteUser,
  });
  const { isMobileMode, isDesktopMode } = useUI();
  const { isSomeProductOverLimit } = useOverLimitProduct();
  const {
    openRequestCard: openOverLimitCard,
    savedRequest,
    onClearOverLimitInDB,
    onSaveOverLimitInDB,
  } = useRequest(null, true);
  const { openRequestCard: openShippingRequestCard } = useRequest();
  const { onAddToMyKits } = useAddToKits(placedOrder.id);

  const isRecurringOrder = useMemo(() => !placedOrder.order_sn, [placedOrder]);

  useEffect(() => {
    if (
      isSomeProductOverLimit &&
      !moreThanInStock &&
      !isObjEmpty(productsObj) &&
      !savedRequest
    ) {
      openOverLimitCard();
    }
  }, [
    isSomeProductOverLimit,
    savedRequest,
    moreThanInStock,
    productsObj,
    openOverLimitCard,
  ]);

  if (isLoading) {
    return <Spinner height="calc(100vh - 350px)" />;
  }

  return (
    <>
      <OrderInfoPageHeader
        isDesktopMode={isDesktopMode}
        placedOrder={placedOrder}
        isRecurringOrder={isRecurringOrder}
        isDistroOrder={isDistroOrder}
        onAddToMyKits={onAddToMyKits}
        classes={classes}
      />
      <div className={classes.shippingInfo}>
        <ShippingAddresses
          className={classes.shippingTitle}
          isOrderPlaced={!!placedOrder?.id}
          isDistroOrder={isDistroOrder}
          isReviewOrderPage={true}
          shippingAddresses={shippingAddresses}
          goToAddresses={goToAddresses}
          onDeleteAddress={onDeleteAddress}
        />
        <div>
          <Typography
            className={classes.shippingTitle}
            variant="h3"
            data-testid="shipping-method-header">
            Shipping Method
          </Typography>
          <ShippingCard
            className={classes.shippingCard}
            isOrderPlaced={!!placedOrder.id}
            onChange={goToSippingDetails}
            title={shippingDetails.method?.name}
            dataTestId="shipping-method-title">
            <ShippingRequestEditButton
              shippingDetails={shippingDetails}
              placedOrder={placedOrder}
              isAvailableMethod={isAvailableMethod}
              userWithoutAllLimits={userWithoutAllLimits}
              isMobileMode={isMobileMode}
              isDistroOrder={isDistroOrder}
              openShippingRequestCard={openShippingRequestCard}
              classes={classes}
            />
            <ShippingMethodUnavailableError
              placedOrder={placedOrder}
              isAvailableMethod={isAvailableMethod}
              classes={classes}
            />
          </ShippingCard>
          <PendingApprovalLabel placedOrder={placedOrder} classes={classes} />
        </div>
        <PlacedOrderInstructions placedOrder={placedOrder} classes={classes} />
      </div>
      <OrderInstructionsSelect
        placedOrder={placedOrder}
        shippingDetails={shippingDetails}
        onChangeInstructions={onChangeInstructions}
        classes={classes}
      />
      <ProductsTitle
        placedOrder={placedOrder}
        withOverLimitRequest={withOverLimitRequest}
        savedRequest={savedRequest}
        openOverLimitCard={openOverLimitCard}
        classes={classes}
      />
      <CartCardsList
        placedOrder={placedOrder}
        cart={cart}
        shippingAddresses={shippingAddresses}
        disabledCountBtn={disabledCountBtn}
        onClearOverLimitInDB={onClearOverLimitInDB}
        onSaveOverLimitInDB={onSaveOverLimitInDB}
        productsObj={productsObj}
        classes={classes}
      />
    </>
  );
};

export default OrderInfo;
